import React, { useCallback } from "react";
import Skeleton from "react-loading-skeleton";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
	Box,
	Button,
	Container,
	Divider,
	Flex,
	Group,
	Image,
	Paper,
	SimpleGrid,
	Text,
	Title,
} from "@mantine/core";
import { useQuery } from "@tanstack/react-query";

import AlertDialog from "@/components/AlertDialog";
import { TitleContainer } from "@/components/HeaderContainer";

import { logout } from "@/actions/auth";
import { useFetchUserData } from "@/hooks/useFetchUserData";
import { clearCheckout, clearCheckoutAnalyticsData } from "@/reducers/courseRegisterSlice";

import avatarApi from "@/services/avatar-api";
import generalApi from "@/services/general-api";

import { getErrorMessage } from "@/utilities/error";
import { removeLogGTMUser } from "@/utilities/googleTagManager";

import { ReactComponent as EditIcon } from "@/assets/img/Profile/avatar_add.svg";
import { ReactComponent as UserNewIcon } from "@/assets/img/Profile/avatar_new.svg";
import { ReactComponent as Book } from "@/assets/img/Profile/book.svg";
import { ReactComponent as CertNew } from "@/assets/img/Profile/cert_new.svg";

function Profile() {
	const { user: userData, isHyperAdminLogin } = useSelector((state) => state.auth);
	const query = useQuery({
		queryKey: ["upcoming-courses"],
		queryFn: () => {
			return generalApi.getUpcomingCourse();
		},
	});
	const total_upcoming_course = query.data?.upcoming?.length || 0;
	const total_completed_course = query.data?.completed?.length || 0;
	const [isDialogOpen, setIsDialogOpen] = React.useState(false);
	const currentUser = userData && userData.user;
	const dispatch = useDispatch();
	let navigate = useNavigate();
	const logOut = useCallback(() => {
		removeLogGTMUser();
		dispatch(logout(isHyperAdminLogin, userData));
		dispatch(clearCheckout());
		dispatch(clearCheckoutAnalyticsData());
		navigate("/login");
	}, [dispatch]);

	useFetchUserData({
		onError: (error) => {
			console.log("error", error);
			toast.error(getErrorMessage(error));
		},
	});

	return (
		<Box>
			<TitleContainer>{"Profile"}</TitleContainer>
			<Box bg="white" py={50} pb={100}>
				<Container maw={764}>
					<Flex
						direction={{ base: "column", md: "row" }}
						gap={{ base: 0, md: 50 }}
						align="center"
						justify="center">
						<AvatarComponent />
						<Flex
							gap={0}
							direction="column"
							align={{
								base: "center",
								md: "start",
							}}>
							<Title order={2} c="black">
								{currentUser.learnermaster_name}
							</Title>
							<Text c="black">{currentUser.learnermaster_email}</Text>
							<Flex
								mt={10}
								direction={{ base: "column", md: "row" }}
								align={{ base: "center", md: "start" }}
								gap={{
									base: 0,
									md: 30,
								}}>
								<Group gap={"sm"} component={Link} to="/certification">
									<CertNew width={20} height={20} />
									{total_completed_course ? (
										<Text>{total_completed_course} Certificate</Text>
									) : (
										<Text>{total_completed_course} Certificate</Text>
									)}
									<i className="fa fa-external-link"></i>
								</Group>
								<Group gap={"sm"} component={Link} to="/course">
									<Book width={20} height={20} color="#0085CA" />
									{total_upcoming_course ? (
										<Text>{total_upcoming_course} Upcoming Courses</Text>
									) : (
										<Text>{total_upcoming_course} Upcoming Courses</Text>
									)}
									<i className="fa fa-external-link"></i>
								</Group>
							</Flex>
						</Flex>
					</Flex>
				</Container>
				<Container maw={1024}>
					<Divider my={20} w={"70%"} />
				</Container>
				<Container maw={764}>
					<SimpleGrid
						cols={{
							base: 1,
							md: 2,
							lg: 3,
						}}
						mt={20}>
						<ProfilePageButton component={Link} to={"/profile/edit"}>
							Update Profile
						</ProfilePageButton>
						<ProfilePageButton component={Link} to={"/profile/change-password"}>
							Change Password
						</ProfilePageButton>
						<ProfilePageButton component={Link} to="/profile/change-email">
							Change Email
						</ProfilePageButton>
						<ProfilePageButton component={Link} to="/cert_orders">
							My Cert Orders
						</ProfilePageButton>
						<ProfilePageButton onClick={() => setIsDialogOpen(true)}>Log out</ProfilePageButton>
						<ProfilePageButton color="red" component={Link} to="/profile/delete-account">
							Delete Account
						</ProfilePageButton>
					</SimpleGrid>
				</Container>
			</Box>

			<AlertDialog
				isOpen={isDialogOpen}
				title={"Logout"}
				description={"Are you sure to logout!"}
				onCancel={() => setIsDialogOpen(false)}
				onAgree={() => logOut()}
			/>
		</Box>
	);
}

const AvatarComponent = () => {
	const avatarQuery = useQuery({
		queryKey: ["learner-avatar"],
		queryFn: () => {
			return avatarApi.getLearnerAvatars();
		},
	});

	const avatarData = avatarQuery.data?.data;

	return (
		<Box
			style={{ position: "relative", width: "fit-content" }}
			component={Link}
			to="/avatar"
			mb={20}>
			{avatarQuery.isLoading ? (
				<Box>
					<Skeleton height={150} width={150} circle />
				</Box>
			) : avatarData ? (
				<>
					<Box pos="relative">
						<Paper
							shadow="xl"
							style={{
								position: "relative",
								width: 150,
								height: 150,
								overflow: "hidden",
								borderRadius: "100%",
								border: "2px solid white",
							}}>
							<Box
								style={{
									width: "100%",
									height: "100%",
									// width: 300,
									// height: 300,
									position: "relative",
									backgroundImage: `url(${avatarData.avatar_background_full_image})`,
									backgroundSize: "cover",
									// transform: "translate(-50%, -50%)",
									// top: "50%",
									// left: "50%",
								}}>
								<Image
									src={avatarData.avatar_occupation_full_image}
									style={{ width: "100%", height: "100%", objectFit: "contain" }}
								/>
							</Box>
						</Paper>
						<Box
							bg="white"
							style={{
								position: "absolute",
								bottom: 0,
								right: 0,
								borderRadius: 50,
							}}>
							<EditIcon height={30} width={30} />
						</Box>
					</Box>
				</>
			) : (
				<Box>
					<Box bg="#edf4f3" bdrs={"100%"} p={15}>
						<UserNewIcon height={120} width={120} color="#34AA80" />
					</Box>
					<Box
						bg="white"
						style={{
							position: "absolute",
							bottom: 0,
							right: 0,
							borderRadius: 50,
						}}>
						<EditIcon height={30} width={30} />
					</Box>
				</Box>
			)}
		</Box>
	);
};

const ProfilePageButton = Button.withProps({
	color: "themed.8",
	variant: "outline",
	bdrs: "sm",
});

export default Profile;
